/**
 * @voltxampere/engine — Dedicated AC source stamp (Sprint 20)
 *
 * Sprint 20 closes D-20-3 by giving the `ac-source` schema type a
 * dedicated stamp. The dispatch is structurally identical to a
 * voltage source's AC stamp — augmented row, ±1 KCL/KVL real cells
 * — but reads its phasor from the dedicated `amplitude` /
 * `phase` (degrees) fields instead of the legacy
 * `acMagnitude`/`acPhase` slots that piggyback on
 * `voltage-source`.
 *
 * `dcOffset` is intentionally ignored here: the DC pre-pass uses it
 * (Sprint 21+: the DC stamp for `ac-source` will add it as a series
 * voltage). At AC the small-signal phasor is what matters.
 *
 * `freq` is a transient-only hint and ignored by the AC sweep.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { getNumericParam, getTerminalNetId } from "../terminal-utils.js";
const DEG_TO_RAD = Math.PI / 180;
export const acSourceACStamp = (ctx, component) => {
    const amplitude = getNumericParam(component, "amplitude");
    const phaseDeg = readOptionalParam(component.params, "phase") ?? 0;
    const pos = ctx.nodeIndex(getTerminalNetId(component, "pos"));
    const neg = ctx.nodeIndex(getTerminalNetId(component, "neg"));
    // Sprint 24 B1 — vSourceIndex returns the absolute MNA row.
    const k = ctx.vSourceIndex(component.id);
    // Augmented MNA pattern (same shape as a voltage source).
    ctx.addY(pos, k, 1, 0);
    ctx.addY(neg, k, -1, 0);
    ctx.addY(k, pos, 1, 0);
    ctx.addY(k, neg, -1, 0);
    if (amplitude === 0)
        return; // Phasor zero — RHS contribution skipped.
    const phaseRad = phaseDeg * DEG_TO_RAD;
    const re = amplitude * Math.cos(phaseRad);
    const im = amplitude * Math.sin(phaseRad);
    ctx.addRhs(k, re, im);
};
function readOptionalParam(params, key) {
    const v = params[key];
    return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}
//# sourceMappingURL=ac-source.js.map